import Image from 'next/image'
import { usePathname } from 'next/navigation'
import Link from 'next/link'
import useAuth from '@/hooks/useAuth'
import { FooterLogo } from '@/utils/Icons'
import {
  PhoneIcon,
  EmailIcon,
  AddressIcon
} from '@/components/Icons/FooterIcons'
import { useRouter } from 'next/router';
import { useTranslation } from 'next-i18next';
import React, { memo, useMemo, lazy, Suspense } from 'react'

const LazyFooterDisclosure = lazy(() => import('./Footer/FooterDisclosure'));

interface Props {
  settings: any
  featured_categories: any
}

// Reusable link item
const FooterLink = memo(({ href, children, className = '' }: { href: string; children: React.ReactNode; className?: string }) => (
  <li className={className}>
    <Link href={href}>{children}</Link>
  </li>
));
FooterLink.displayName = 'FooterLink';

// Social icon (still uses Image, ok)
const SocialIcon = memo(({ social }: { social: any }) => {
  const imageUrl = `${process.env.NEXT_PUBLIC_SITE_URL}/storage/${social.icon}`;
  return (
    <a
      href={social.url}
      className="border-2 border-success-500 rounded-md p-1 inline-block cursor-pointer text-success-500"
      target="_blank"
      rel="noopener noreferrer"
      aria-label={`Visit our ${social.name || 'social'} page`}
    >
      <Image
        src={imageUrl}
        width={24}
        height={24}
        alt="social-icon"
        loading="lazy"
        className="fill-current"
      />
    </a>
  );
});
SocialIcon.displayName = 'SocialIcon';

// --- Desktop sections with t passed as prop ---
const ContactInfoDesktop = memo(({ settings, locale, t }: { settings: any; locale: string; t: any }) => {
  const phone = settings?.phone;
  const email = settings?.email;
  const address = locale === 'ar' ? settings?.address?.ar : settings?.address?.en;
  const socials = settings?.social_medias || [];

  return (
    <div className="hidden lg:block contact-info mt-6">
      <h3 className="text-success font-semibold text-xl">{t('contactUs')}</h3>
      <ul className="mt-4">
        {phone && (
          <li className="flex items-center mt-2">
            <span><PhoneIcon /></span>
            <a href={`tel:${phone}`} className={locale === 'ar' ? 'mr-6' : 'ml-3'}>{phone}</a>
          </li>
        )}
        {email && (
          <li className="flex items-center mt-2">
            <span><EmailIcon /></span>
            <a href={`mailto:${email}`} className={locale === 'ar' ? 'mr-4' : 'ml-3'}>{email}</a>
          </li>
        )}
        {address && (
          <li className="flex items-center mt-2">
            <span><AddressIcon /></span>
            <span className={locale === 'ar' ? 'mr-6' : 'ml-3'}>{address}</span>
          </li>
        )}
      </ul>
      <div className="hidden lg:block socials mt-8 lg:mt-4">
        <ul className="flex items-center">
          {socials.map((social: any, index: number) => (
            <li key={index} className={index > 0 ? (locale === 'ar' ? 'mr-6' : 'ml-3') : ''}>
              <SocialIcon social={social} />
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
});
ContactInfoDesktop.displayName = 'ContactInfoDesktop';

const SocialIconsMobile = memo(({ socials }: { socials: any[] }) => (
  <div className="block lg:hidden socials mt-8 lg:mt-4">
    <ul className="flex justify-center gap-[24px]">
      {socials.map((social: any, index: number) => (
        <li key={index}>
          <SocialIcon social={social} />
        </li>
      ))}
    </ul>
  </div>
));
SocialIconsMobile.displayName = 'SocialIconsMobile';

const CategoryLinksDesktop = memo(({ categories, locale, t }: { categories: any[]; locale: string; t: any }) => {
  if (!categories?.length) return null;
  return (
    <div className="hidden lg:block links mt-6">
      <h3 className="text-success font-semibold text-xl">{t('categories')}</h3>
      <ul className="mt-4">
        {categories.map((category: any) => (
          <FooterLink key={category.id} href={`/products?categories=${category.slug}`} className="mt-2">
            {locale === 'ar' ? category.name?.ar : category.name?.en}
          </FooterLink>
        ))}
      </ul>
    </div>
  );
});
CategoryLinksDesktop.displayName = 'CategoryLinksDesktop';

const CustomerCareDesktop = memo(({ user, t }: { user: any; t: any }) => {
  const links = [
    { name: t('shoppingCart'), href: '/cart' },
    ...(user ? [
      { name: t('myAccount'), href: '/account' },
      { name: t('address'), href: '/account/address' },
      { name: t('myOrders'), href: '/account/orders' },
      { name: t('wishlist'), href: '/account/wishlist' },
    ] : [])
  ];

  if (links.length === 0) return null;

  return (
    <div className="hidden lg:block links mt-6">
      <h3 className="text-success font-semibold text-xl">{t('customerCare')}</h3>
      <ul className="mt-4">
        {links.map((link, idx) => (
          <FooterLink key={idx} href={link.href} className="mt-2">
            {link.name}
          </FooterLink>
        ))}
      </ul>
    </div>
  );
});
CustomerCareDesktop.displayName = 'CustomerCareDesktop';

const GeneralLinksDesktop = memo(({ t }: { t: any }) => {
  const links = [
    { name: t('home'), href: '/' },
    { name: t('contactUs'), href: '/contactus' },
    { name: t('aboutUs'), href: '/aboutus' },
    { name: t('policies'), href: '/policies' },
    { name: t('deliveryPolicy'), href: '/delivery_policy' },
  ];

  return (
    <div className="hidden lg:block links mt-6">
      <h3 className="text-success font-semibold text-xl">{t('publicLinks')}</h3>
      <ul className="mt-4">
        {links.map((link, idx) => (
          <FooterLink key={idx} href={link.href} className="mt-2">
            {link.name}
          </FooterLink>
        ))}
      </ul>
    </div>
  );
});
GeneralLinksDesktop.displayName = 'GeneralLinksDesktop';

// Main Footer component
const Footer = memo(({ settings, featured_categories }: Props) => {
  const { user } = useAuth('none');
  const path = usePathname();
  const { t } = useTranslation('footer');
  const { locale } = useRouter();
  const userLocale = locale === 'ar' ? 'ar' : 'en';

  // Memoized data
  const customerCareLinks = useMemo(() => [
    { name: t('shoppingCart'), href: '/cart' },
    ...(user ? [
      { name: t('myAccount'), href: '/account' },
      { name: t('address'), href: '/account/address' },
      { name: t('myOrders'), href: '/account/orders' },
      { name: t('wishlist'), href: '/account/wishlist' },
    ] : [])
  ], [user, t]);

  const generalLinks = useMemo(() => [
    { name: t('home'), href: '/' },
    { name: t('contactUs'), href: '/contactus' },
    { name: t('aboutUs'), href: '/aboutus' },
    { name: t('policies'), href: '/policies' },
    { name: t('deliveryPolicy'), href: '/delivery_policy' },
  ], [t]);

  const aboutText = useMemo(() => {
    if (path === '/delivery_policy') return t('aboutParagraph');
    return locale === 'ar' ? settings.about?.ar : settings.about?.en;
  }, [path, locale, settings.about, t]);

  const categoryItems = useMemo(() => featured_categories, [featured_categories]);

  return (
    <footer className="bg-primary-700 text-white pt-[40px] pb-3 lg:pt-6">
      <div className="footer-container container-fluid" dir={locale === 'ar' ? 'rtl' : 'ltr'}>
        <div className="footer-about">
          <div className="footer-logo">
            <FooterLogo />
          </div>
          <p className="mt-[24px] mb-[16px] lg:mb-0 lg:mt-2 font-light xl:max-w-[90%] 2xl:max-w-[80%]">
            {aboutText}
          </p>
        </div>

        {/* Desktop Categories */}
        <CategoryLinksDesktop categories={categoryItems} locale={userLocale} t={t} />

        {/* Mobile Categories - Lazy loaded */}
        <Suspense fallback={<div className="h-16" />}>
          <LazyFooterDisclosure title={t('categories')} links={categoryItems} />
        </Suspense>

        {/* Desktop Customer Care */}
        <CustomerCareDesktop user={user} t={t} />

        {/* Mobile Customer Care - Lazy loaded */}
        <Suspense fallback={<div className="h-16" />}>
          <LazyFooterDisclosure title={t('customerCare')} links={customerCareLinks} />
        </Suspense>

        {/* Desktop General Links */}
        <GeneralLinksDesktop t={t} />

        {/* Mobile General Links - Lazy loaded */}
        <Suspense fallback={<div className="h-16" />}>
          <LazyFooterDisclosure title={t('publicLinks')} links={generalLinks} />
        </Suspense>

        {/* Mobile Contact - Lazy loaded */}
        <Suspense fallback={<div className="h-16" />}>
          <LazyFooterDisclosure title={t('contactUs')} settings={settings} />
        </Suspense>

        {/* Desktop Contact Info */}
        <ContactInfoDesktop settings={settings} locale={userLocale} t={t} />

        {/* Mobile Social Icons */}
        <SocialIconsMobile socials={settings?.social_medias || []} />
      </div>

      <div className="footer-credits container-fluid pt-3 mt-4 mb-2 text-center font-light capitalize">
        <p>
          Copyright {new Date().getFullYear()}{' '}
          <a href="https://nazadv.com" className="font-semibold text-center">
            BASMTI
          </a>{' '}
          . All rights reserved
        </p>
      </div>
    </footer>
  );
});

Footer.displayName = 'Footer';

export default Footer;